import { useState } from "react";
import { Wallet, Loader2 } from "lucide-react";
import { useLoad } from "@/lib/api";
import { useMe } from "@/lib/me";
import { gbp } from "@/lib/format";
import { preset, rangeLabel, rangeQuery } from "@/lib/dates";
import { PageHead, Empty } from "@/components/Bits";
import DateRange from "@/components/DateRange";

const Period = ({ title, range, testid }) => {
  const s = useLoad(`/spend?${rangeQuery(range)}`);
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 rise" data-testid={testid}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{title}</p>
      <p className="text-xs text-slate-400">{rangeLabel(range)}</p>
      {!s.data ? <Loader2 className="mt-4 h-5 w-5 animate-spin text-slate-300" /> : (
        <>
          <p className="mt-3 font-display text-3xl font-extrabold" data-testid={`${testid}-net`}>{gbp(s.data.net)}</p>
          <p className="text-xs text-slate-500">spent after credits</p>
          <div className="mt-4 space-y-1 border-t border-slate-100 pt-3 text-sm">
            <p className="flex justify-between"><span className="text-slate-500">Invoices ({s.data.invoice_count})</span><span className="font-mono" data-testid={`${testid}-invoices`}>{gbp(s.data.invoices)}</span></p>
            <p className="flex justify-between"><span className="text-slate-500">Credits received</span><span className="font-mono text-emerald-700" data-testid={`${testid}-received`}>−{gbp(s.data.received)}</span></p>
            <p className="flex justify-between"><span className="text-slate-500">Credits not received yet</span><span className="font-mono text-amber-700" data-testid={`${testid}-outstanding`}>{gbp(s.data.outstanding)}</span></p>
          </div>
        </>
      )}
    </div>
  );
};

const Breakdown = ({ range }) => {
  const s = useLoad(`/spend?${rangeQuery(range)}`);
  if (!s.data) return <Loader2 className="h-5 w-5 animate-spin text-slate-300" />;
  if (!s.data.by_supplier.length) return <Empty icon={Wallet} title="Nothing in this period" text="Uploaded invoices and credit claims dated in this period appear here." testid="spend-empty" />;
  const cell = "px-4 py-2.5 text-right font-mono";
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
      <table className="w-full min-w-[640px] text-sm" data-testid="spend-table">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
          <tr><th className="px-4 py-2.5">Wholesaler</th><th className="px-4 text-right">Invoices</th><th className="px-4 text-right">Credits claimed</th><th className="px-4 text-right">Received</th><th className="px-4 text-right">Not received</th><th className="px-4 text-right">Net spent</th></tr>
        </thead>
        <tbody>
          {s.data.by_supplier.map((r) => (
            <tr key={r.supplier} className="border-t border-slate-100" data-testid={`spend-row-${r.supplier}`}>
              <td className="px-4 py-2.5 font-semibold">{r.supplier}</td>
              <td className={cell}>{gbp(r.invoices)} <span className="text-[10px] text-slate-400">×{r.invoice_count}</span></td>
              <td className={cell}>{gbp(r.claimed)}</td>
              <td className={`${cell} text-emerald-700`}>{gbp(r.received)}</td>
              <td className={`${cell} ${r.outstanding ? "text-amber-700" : ""}`}>{gbp(r.outstanding)}{r.outstanding_count > 0 && <span className="text-[10px]"> ({r.outstanding_count})</span>}</td>
              <td className={`${cell} font-semibold`}>{gbp(r.net)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold" data-testid="spend-total-row">
            <td className="px-4 py-2.5">Total</td><td className={cell}>{gbp(s.data.invoices)}</td><td className={cell}>{gbp(s.data.claimed)}</td>
            <td className={cell}>{gbp(s.data.received)}</td><td className={cell}>{gbp(s.data.outstanding)}</td><td className={cell}>{gbp(s.data.net)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default function SpendPage() {
  const ys = useMe().settings?.year_start_month || 4;
  const [range, setRange] = useState(() => ({ ...preset("this_month", ys) }));
  return (
    <div>
      <PageHead title="Spend" sub="Invoices minus credits received, by invoice date. A credit counts as received once it's on a wholesaler statement." />
      <div className="mb-8 grid gap-3 md:grid-cols-3">
        <Period title="This month" range={preset("this_month", ys)} testid="spend-month" />
        <Period title="This quarter" range={preset("this_quarter", ys)} testid="spend-quarter" />
        <Period title={ys === 1 ? "This year" : "This financial year"} range={preset("this_year", ys)} testid="spend-year" />
      </div>
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-bold">By wholesaler</h2>
        <DateRange value={range} onChange={setRange} testid="spend-range" className="sm:w-96" />
      </div>
      <Breakdown range={range} />
    </div>
  );
}
